import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { X, Clock } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';

import { timeService } from '@/services/api';

interface TimeCorrectionModalProps {
  open: boolean;
  onClose: () => void;
  /** When provided, correction is submitted via PUT /time-logs/:logId/correct. */
  logId?: string;
  /** Prefilled duration in minutes when correcting a specific log. */
  initialDuration?: number;
}

type CorrectionType = 'missed_clockin' | 'missed_clockout' | 'wrong_time' | 'other';

/**
 * TimeCorrectionModal
 * Form for requesting time entry corrections. Uses backend PUT /time-logs/:logId/correct when logId is provided.
 */
const TimeCorrectionModal: React.FC<TimeCorrectionModalProps> = ({ open, onClose, logId, initialDuration }) => {
  const [correctionType, setCorrectionType] = useState<CorrectionType>('missed_clockin');
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [originalTime, setOriginalTime] = useState<Dayjs | null>(null);
  const [correctedTime, setCorrectedTime] = useState<Dayjs | null>(null);
  const [durationMinutes, setDurationMinutes] = useState<number>(initialDuration ?? 0);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open && initialDuration != null) setDurationMinutes(initialDuration);
  }, [open, initialDuration]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    let targetLogId = logId;
    if (!targetLogId && date) {
      const start = date.startOf('day').format('YYYY-MM-DD');
      const end = date.endOf('day').format('YYYY-MM-DD');
      const logs = await timeService.getTimeLogs({ startDate: start, endDate: end });
      if (logs.length > 0) targetLogId = logs[0].id;
    }
    if (!targetLogId) {
      setSubmitError('No time log found for the selected date. Record time first or select a log from your time log list.');
      setIsSubmitting(false);
      return;
    }
    const duration = durationMinutes > 0 ? durationMinutes : (correctedTime && date ? correctedTime.diff(date.startOf('day'), 'minute') : 60);
    const finalDuration = Math.max(1, duration);
    try {
      await timeService.correctTimeLog(targetLogId, { duration: finalDuration, reason: reason.trim() });
      setSubmitted(true);
      setTimeout(() => handleClose(), 1500);
    } catch (e) {
      setSubmitError((e as Error).message ?? 'Failed to submit correction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCorrectionType('missed_clockin');
    setDate(dayjs());
    setOriginalTime(null);
    setCorrectedTime(null);
    setDurationMinutes(initialDuration ?? 0);
    setReason('');
    setSubmitted(false);
    setSubmitError(null);
    onClose();
  };

  const correctionTypes = [
    { value: 'missed_clockin', label: 'Missed Clock In' },
    { value: 'missed_clockout', label: 'Missed Clock Out' },
    { value: 'wrong_time', label: 'Wrong Time Recorded' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Clock size={20} />
              <Typography variant="h6" fontWeight={600}>
                Time Correction Request
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {submitted ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Time correction request submitted successfully!
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              {/* Current Status */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="Today's Hours: 7h 30m" color="primary" variant="outlined" size="small" />
                <Chip label="This Week: 32h 15m" color="default" variant="outlined" size="small" />
              </Box>

              {/* Correction Type */}
              <FormControl fullWidth>
                <InputLabel>Correction Type</InputLabel>
                <Select
                  value={correctionType}
                  label="Correction Type"
                  onChange={(e) => setCorrectionType(e.target.value as CorrectionType)}
                >
                  {correctionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Date */}
              <DatePicker
                label="Date"
                value={date}
                onChange={setDate}
                maxDate={dayjs()}
                slotProps={{
                  textField: { fullWidth: true, size: 'medium' },
                }}
              />

              {/* Time Fields */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {correctionType === 'wrong_time' && (
                  <TimePicker
                    label="Original Time"
                    value={originalTime}
                    onChange={setOriginalTime}
                    slotProps={{
                      textField: { fullWidth: true, size: 'medium' },
                    }}
                  />
                )}
                <TimePicker
                  label={correctionType === 'wrong_time' ? 'Correct Time' : 'Time'}
                  value={correctedTime}
                  onChange={setCorrectedTime}
                  slotProps={{
                    textField: { fullWidth: true, size: 'medium' },
                  }}
                />
              </Box>

              {/* Duration (minutes) - used when submitting correction to API */}
              <TextField
                label="Duration (minutes)"
                type="number"
                inputProps={{ min: 1, max: 1440 }}
                value={durationMinutes || ''}
                onChange={(e) => setDurationMinutes(Math.max(0, parseInt(e.target.value, 10) || 0))}
                fullWidth
                helperText="Corrected total duration for this log"
              />

              {/* Reason */}
              <TextField
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={3}
                fullWidth
                required
                placeholder="Please explain why you need this time correction..."
              />

              {submitError && (
                <Alert severity="error" onClose={() => setSubmitError(null)}>
                  {submitError}
                </Alert>
              )}

              {/* Info */}
              <Alert severity="info">
                Time correction requests require manager approval. Please ensure your reason
                is clear and accurate.
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || submitted || !date || !reason || (!durationMinutes && !correctedTime)}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TimeCorrectionModal;
